// główna wyszukiwarka produktów – lokalny override (pełna karta, bez extend)
// Zmiany: kategoria nigdy nie wysyłana do API; applyProductsParameters(categoryId?, url_link?); placeholder Szukaj/Szukaj produktu; bez dropdownu kategorii
// @ts-nocheck

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  Dispatch,
  SetStateAction
} from 'react';
import { ArrowLeft } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { useLocation } from 'react-router-dom';
import classnames from 'classnames';

import { reduxActions, useDispatch, useSelector } from 'store';
import { useAppNavigate, useRWD } from 'hooks';
import { useGetSearchCategories, useGetSearchProducts, useGetSearchFilterCategoryList } from 'api';

import styles from 'theme/components/containers/Search/Search.module.scss';
import DesktopOverlay from 'components/containers/Search/DektopOverlay';
import MobileOverlay from 'components/containers/Search/MobileOverlay';

import { SearchIcon, XIcon } from 'assets/icons';

type IQueryParams = {
  page: number;
  limit: number;
  search_keyword: string;
  categoryId: number | null;
};

const Search = ({
  trigerReset,
  setTriggerReset
}: {
  trigerReset?: boolean;
  setTriggerReset?: Dispatch<SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const location = useLocation();

  const [showCategories, setShowCategories] = useState<boolean>(false);
  const { isMobile } = useRWD();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isFocus, setIsFocus] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [queryParams, setQueryParams] = useState<IQueryParams>({
    page: 1,
    limit: 5,
    search_keyword: '',
    categoryId: null
  });

  const {
    searchKeyword: globalSearchKeyboard,
    searchFilterCategory: globalSearchFilterCategory,
    categoryId: globalCategoryId
  } = useSelector((state) => state.products);

  // parametry do wyszukiwania produktów – kategoria nigdy nie jest wysyłana (zawsze pełna kartoteka)
  const searchProductsParams = useMemo(
    () => (queryParams ? { ...queryParams, categoryId: null } : undefined),
    [queryParams?.page, queryParams?.limit, queryParams?.search_keyword]
  );

  const {
    data: productsData,
    isLoading: isProductsDataLoading,
    refetch: refetchProductsData
  } = useGetSearchProducts(searchProductsParams, { enabled: false });

  const {
    data: categoriesData,
    isLoading: isCategoriesDataLoading,
    refetch: refetchCategoriesData
  } = useGetSearchCategories(
    { page: 1, limit: 999, search_keyword: queryParams.search_keyword },
    { enabled: false }
  );

  const { data: filtersCategoryData } = useGetSearchFilterCategoryList(
    { page: 1, limit: 999, categoryId: globalCategoryId },
    { keepPreviousData: true }
  );

  const searchKeywordDebounce = useCallback(
    debounce(
      (phrase: string) =>
        setQueryParams((prevState) => ({ ...prevState, search_keyword: phrase, page: 1 })),
      1000
    ),
    []
  );

  useEffect(() => {
    if (trigerReset) {
      handleClearButton();
      setTriggerReset?.(false);
    }
  }, [trigerReset, searchKeyword]);

  useEffect(() => {
    const category = filtersCategoryData?.items.find((item: { id: number }) => item.id === globalCategoryId);
    category && dispatch(reduxActions.setSearchFilterCategory(category));
  }, [globalCategoryId, filtersCategoryData]);

  useEffect(() => {
    searchKeywordDebounce(searchKeyword);
  }, [searchKeyword]);

  const setGlobalKeyword = () => setSearchKeyword(globalSearchKeyboard);

  useEffect(() => {
    setGlobalKeyword();
  }, [globalSearchKeyboard]);

  useEffect(() => {
    if (queryParams.search_keyword) {
      refetchCategoriesData();
      refetchProductsData();
    }
  }, [queryParams]);

  useEffect(() => {
    dispatch(reduxActions.setSearchKeyword(searchKeyword));
    setIsFocus(false);
  }, [location]);

  useEffect(() => {
    if (!isMobile) return;

    document.body.classList.toggle('mobile-search-open', isFocus);

    return () => {
      document.body.classList.remove('mobile-search-open');
    };
  }, [isFocus, isMobile]);

  const applyProductsParameters = (categoryId?: number, url_link?: string) => {
    dispatch(reduxActions.setSearchKeyword(searchKeyword));
    setIsFocus(false);
    const goToCategory = categoryId != null && url_link;
    dispatch(reduxActions.setCategoryId(goToCategory ? categoryId : undefined));
    dispatch(
      reduxActions.setSearchFilterCategory(
        goToCategory ? { id: categoryId, name: '', url_link } : { id: null, name: '', url_link: null }
      )
    );
    navigate(
      goToCategory ? `/${url_link}?search_keyword=${searchKeyword}` : `/products?search_keyword=${searchKeyword}`
    );
  };

  const handleClearButton = () => {
    setSearchKeyword('');
    dispatch(reduxActions.setSearchKeyword(''));
  };

  return (
    <div
      className={classnames(styles.componentWrapper, { [styles.focus]: isFocus }, 'StylePath-Components-Containers-Search')}
      ref={wrapperRef}
    >
      <ArrowLeft
        className={classnames(styles.icon, { [styles.hide]: !(isFocus && isMobile) })}
        onClick={() => setIsFocus(false)}
      />
      <input
        placeholder={isMobile ? t('Szukaj') : t('Szukaj produktu')}
        value={searchKeyword}
        onChange={(event) => setSearchKeyword(event.target.value)}
        onKeyUp={(event) => {
          if (event.key === 'Enter') {
            applyProductsParameters();
            if (event.target instanceof HTMLInputElement) event.target.blur();
          }
        }}
        onClick={() => {
          setIsFocus(true);
          if (isMobile) setShowCategories(true);
        }}
        onFocus={() => {
          setIsFocus(true);
          if (isMobile) setShowCategories(true);
        }}
      />
      {searchKeyword && (
        <button onClick={() => handleClearButton()} className={styles.clearSearchKeyword}>
          <XIcon />
        </button>
      )}
      <div className={styles.icon} onClick={() => applyProductsParameters()}>
        <SearchIcon />
      </div>
      {isFocus &&
        (isMobile ? (
          <MobileOverlay
            categoriesData={categoriesData}
            isCategoriesDataLoading={isCategoriesDataLoading}
            setShowCategories={setShowCategories}
            showCategories={showCategories}
            productsData={productsData}
            isProductsDataLoading={isProductsDataLoading}
            searchKeyword={searchKeyword}
            setGlobalKeyword={setGlobalKeyword}
            isFocus={isFocus}
            setIsFocus={setIsFocus}
            applyProductsParameters={applyProductsParameters}
          />
        ) : (
          <DesktopOverlay
            categoriesData={categoriesData}
            isCategoriesDataLoading={isCategoriesDataLoading}
            productsData={productsData}
            isProductsDataLoading={isProductsDataLoading}
            searchKeyword={searchKeyword}
            setGlobalKeyword={setGlobalKeyword}
            setIsFocus={setIsFocus}
            applyProductsParameters={applyProductsParameters}
            wrapperRef={wrapperRef}
          />
        ))}
    </div>
  );
};

export default Search;
